// ══════════════════════════════════════════════════════════════════════════════
// SatsangPage.js — Satsang Circles
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useRef } from "react";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

// ── Utility ───────────────────────────────────────────────────────────────────
function timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60)   return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

// ── Empty state — no circles yet ──────────────────────────────────────────────
function NoCircles({ onCreateClick, onJoinClick }) {
  return (
    <div style={{ textAlign: "center", padding: "60px 24px" }}>
      <div style={{ fontSize: "3rem", marginBottom: "16px" }}>🕉️</div>
      <div style={{
        fontFamily: "'Inter',sans-serif", fontWeight: 700,
        fontSize: "1.4rem", color: "var(--saffron)",
        marginBottom: "10px",
      }}>
        Your Satsang awaits
      </div>
      <div style={{
        color: "var(--text)",
        fontSize: "1.15rem", lineHeight: 1.7,
        fontFamily: "'Inter',sans-serif",
        maxWidth: 420, margin: "0 auto 36px",
      }}>
        A private circle for family or close friends — the same verse every morning, read together in silence.
      </div>
      <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
        <button onClick={onCreateClick} style={{
          padding: "12px 28px", borderRadius: "10px",
          background: "var(--saffron)",
          border: "none", color: "white",
          fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1rem",
          cursor: "pointer",
        }}>
          Create a Circle
        </button>
        <button onClick={onJoinClick} style={{
          padding: "12px 28px", borderRadius: "10px",
          background: "#ffffff",
          border: "1px solid var(--saffron)", color: "var(--saffron)",
          fontFamily: "'Inter',sans-serif", fontWeight: 600, fontSize: "1rem",
          cursor: "pointer",
        }}>
          Join with a Code
        </button>
      </div>
    </div>
  );
}

// ── Create circle modal ────────────────────────────────────────────────────────
function CreateModal({ user, onClose, onCreated }) {
  const [circleName, setCircleName] = useState("");
  const [displayName, setDisplayName] = useState(user.name || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    if (!circleName.trim()) { setError("Give your circle a name"); return; }
    if (!displayName.trim()) { setError("Enter how you want to appear"); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/circles/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          display_name: displayName.trim(),
          circle_name: circleName.trim(),
        }),
      });
      const d = await res.json();
      if (d.circle_id) onCreated(d);
      else setError("Something went wrong");
    } catch { setError("Could not connect to server"); }
    setLoading(false);
  };

  return (
    <Overlay onClose={onClose}>
      <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.15rem", color: "var(--saffron)", marginBottom: "20px" }}>
        Create a Circle
      </div>
      <Label>Circle name</Label>
      <input
        value={circleName}
        onChange={e => setCircleName(e.target.value)}
        placeholder="e.g. Sharma Family, Morning Satsang"
        style={inputStyle}
      />
      <Label style={{ marginTop: "14px" }}>Your name in this circle</Label>
      <input
        value={displayName}
        onChange={e => setDisplayName(e.target.value)}
        placeholder="e.g. Appa, Didi, Ravi"
        style={inputStyle}
      />
      {error && <div style={{ color: "#dc2626", fontSize: "0.95rem", marginTop: "8px" }}>{error}</div>}
      <button onClick={handleCreate} disabled={loading} style={primaryBtn}>
        {loading ? "Creating..." : "Create →"}
      </button>
    </Overlay>
  );
}

// ── Join circle modal ─────────────────────────────────────────────────────────
function JoinModal({ user, onClose, onJoined }) {
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState(user.name || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleJoin = async () => {
    if (!code.trim()) { setError("Enter the invite code"); return; }
    if (!displayName.trim()) { setError("Enter how you want to appear"); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/circles/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: user.id,
          display_name: displayName.trim(),
          join_code: code.trim().toUpperCase(),
        }),
      });
      const d = await res.json();
      if (d.error) setError(d.error);
      else onJoined(d);
    } catch { setError("Could not connect to server"); }
    setLoading(false);
  };

  return (
    <Overlay onClose={onClose}>
      <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.15rem", color: "var(--saffron)", marginBottom: "20px" }}>
        Join a Circle
      </div>
      <Label>Invite code</Label>
      <input
        value={code}
        onChange={e => setCode(e.target.value.toUpperCase())}
        placeholder="e.g. KARMA7"
        maxLength={6}
        style={{ ...inputStyle, letterSpacing: "0.2em", fontSize: "1.3rem", textTransform: "uppercase" }}
      />
      <Label style={{ marginTop: "14px" }}>Your name in this circle</Label>
      <input
        value={displayName}
        onChange={e => setDisplayName(e.target.value)}
        placeholder="e.g. Amma, Bhai, Priya"
        style={inputStyle}
      />
      {error && <div style={{ color: "#dc2626", fontSize: "0.95rem", marginTop: "8px" }}>{error}</div>}
      <button onClick={handleJoin} disabled={loading} style={primaryBtn}>
        {loading ? "Joining..." : "Join →"}
      </button>
    </Overlay>
  );
}

// ── Created confirmation modal ────────────────────────────────────────────────
function CreatedModal({ circle, onClose }) {
  const [copied, setCopied] = useState(false);

  const shareText = `Join my Satsang Circle on VedSanga!\n\nCircle: ${circle.name}\nInvite code: ${circle.join_code}\n\nDownload VedSanga → vedsanga.com`;

  const copyCode = () => {
    navigator.clipboard.writeText(circle.join_code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Overlay onClose={onClose}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: "2.5rem", marginBottom: "12px" }}>🙏</div>
        <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.15rem", color: "var(--saffron)", marginBottom: "8px" }}>
          Circle created
        </div>
        <div style={{ color: "var(--text-dim)", marginBottom: "24px" }}>
          Share this code with your family or friends
        </div>

        <div style={{
          background: "var(--bg3)", border: "1px solid var(--saffron)",
          borderRadius: "12px", padding: "20px",
          fontFamily: "'Inter',sans-serif", fontWeight: 700,
          fontSize: "2rem", letterSpacing: "0.3em",
          color: "var(--saffron)", marginBottom: "16px",
          cursor: "pointer",
        }} onClick={copyCode}>
          {circle.join_code}
        </div>

        <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "20px" }}>
          {copied ? "✓ Copied!" : "Tap code to copy"}
        </div>

        <button
          onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank")}
          style={{
            width: "100%", padding: "11px", borderRadius: "8px",
            background: "#ffffff",
            border: "1px solid #047857",
            color: "#047857", fontFamily: "'Inter',sans-serif", fontWeight: 600,
            fontSize: "1rem", cursor: "pointer", marginBottom: "10px",
          }}
        >
          📱 Share on WhatsApp
        </button>
        <button onClick={onClose} style={{ ...primaryBtn, marginTop: 0 }}>
          Enter circle →
        </button>
      </div>
    </Overlay>
  );
}

// ── Circle detail view ────────────────────────────────────────────────────────
// ── Milestone share card ──────────────────────────────────────────────────────
function MilestoneCard({ milestone, circleName, onClose }) {
  const cardRef = useRef(null);
  const shareText = `${milestone.emoji} ${circleName} — ${milestone.name}\n\n"${milestone.message}"\n\nReading the Gita together on VedSanga 🙏`;

  const share = () => {
    if (navigator.share) {
      navigator.share({ text: shareText });
    } else {
      navigator.clipboard.writeText(shareText);
    }
  };

  return (
    <Overlay onClose={onClose}>
      <div ref={cardRef} style={{ textAlign: "center" }}>
        <div style={{ fontSize: "3rem", marginBottom: "12px" }}>{milestone.emoji}</div>
        <div style={{
          fontFamily: "'Inter',sans-serif", fontWeight: 700,
          fontSize: "1.15rem", color: "var(--saffron)",
          marginBottom: "6px",
        }}>
          {milestone.name}
        </div>
        <div style={{
          fontFamily: "'Inter',sans-serif", fontWeight: 600,
          fontSize: "0.95rem", color: "var(--gold)",
          marginBottom: "20px",
        }}>
          {circleName}
        </div>
        <div style={{
          background: "rgba(232,131,42,0.08)",
          border: "1px solid rgba(232,131,42,0.3)",
          borderRadius: "12px", padding: "18px",
          color: "var(--text)",
          fontSize: "1.05rem", lineHeight: 1.7, marginBottom: "24px",
        }}>
          "{milestone.message}"
        </div>
        <button onClick={share} style={{
          width: "100%", padding: "12px", borderRadius: "8px",
          background: "#ffffff",
          border: "1px solid #047857",
          color: "#047857", fontFamily: "'Inter',sans-serif", fontWeight: 600,
          fontSize: "1rem", cursor: "pointer", marginBottom: "10px",
        }}>
          📱 Share with family
        </button>
        <button onClick={onClose} style={primaryBtn}>Close</button>
      </div>
    </Overlay>
  );
}

// ── Ancestral Wisdom ──────────────────────────────────────────────────────────
function AncestralWisdom({ circleId, user }) {
  const [wisdoms,   setWisdoms]   = useState([]);
  const [showAdd,   setShowAdd]   = useState(false);
  const [form,      setForm]      = useState({ ancestor_name: "", relationship: "", text: "", occasion: "" });
  const [saving,    setSaving]    = useState(false);
  const [recording, setRecording] = useState(false);
  const [audioURL,  setAudioURL]  = useState("");
  const [audioBlob, setAudioBlob] = useState(null);
  const [playing,   setPlaying]   = useState(null);
  const mediaRef = useRef(null);
  const audioRef = useRef({});

  const load = async () => {
    try {
      const res = await fetch(`${API}/circles/${circleId}/wisdom?user_id=${encodeURIComponent(user.id)}`);
      const d = await res.json();
      setWisdoms(d.wisdom || []);
    } catch {}
  };

  useEffect(() => { load(); }, [circleId]); // eslint-disable-line

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = e => chunks.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioURL(URL.createObjectURL(blob));
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch { alert("Microphone access needed to record."); }
  };

  const stopRecording = () => {
    mediaRef.current?.stop();
    mediaRef.current?.stream?.getTracks().forEach(t => t.stop());
    setRecording(false);
  };

  const handleSave = async () => {
    if (!form.ancestor_name.trim() || !form.text.trim()) return;
    setSaving(true);
    let audio_url = "";
    if (audioBlob) {
      try {
        // Stored on our server under an unguessable name (see POST /upload)
        const { uploadMedia } = await import("./mediaUpload");
        audio_url = await uploadMedia("vedsanga-audio", audioBlob, "wisdom.webm");
      } catch {}
    }
    await fetch(`${API}/circles/${circleId}/wisdom`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user.id, display_name: user.name || "Member", ...form, audio_url }),
    });
    setForm({ ancestor_name: "", relationship: "", text: "", occasion: "" });
    setAudioURL(""); setAudioBlob(null);
    setShowAdd(false); setSaving(false);
    load();
  };

  const togglePlay = (id, url) => {
    if (playing === id) { audioRef.current[id]?.pause(); setPlaying(null); }
    else {
      if (playing && audioRef.current[playing]) audioRef.current[playing].pause();
      if (!audioRef.current[id]) audioRef.current[id] = new Audio(url);
      audioRef.current[id].play();
      audioRef.current[id].onended = () => setPlaying(null);
      setPlaying(id);
    }
  };

  return (
    <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "16px", padding: "22px", marginBottom: "20px", position: "relative", overflow: "hidden", boxShadow: "var(--card-shadow)" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,var(--saffron),var(--gold))" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--gold)" }}>
          🌳 Ancestral wisdom
        </div>
        <button onClick={() => setShowAdd(true)} style={{
          background: "none", border: "1px solid var(--border)", borderRadius: "8px",
          padding: "6px 14px", color: "var(--text-dim)", fontSize: "0.95rem", cursor: "pointer",
        }}>+ Add</button>
      </div>

      <div style={{
        background: "var(--bg3)", border: "1px solid var(--border)",
        borderRadius: "10px", padding: "12px 14px", marginBottom: "18px",
        fontSize: "1rem", color: "var(--text)", lineHeight: 1.7,
      }}>
        🔒 Private to your circle only · Never shared outside · Voice stored securely · Never used for AI training
      </div>

      {wisdoms.length === 0 && (
        <div style={{ color: "var(--text)", fontSize: "1.05rem" }}>
          Preserve your family's wisdom here — words your elders passed down that should never be forgotten.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {wisdoms.map(w => (
          <div key={w.id} style={{ borderLeft: "2px solid var(--gold)", paddingLeft: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", flexWrap: "wrap" }}>
              <span style={{ fontSize: "1rem", color: "var(--saffron)", fontFamily: "'Inter',sans-serif", fontWeight: 700 }}>
                {w.ancestor_name}
              </span>
              <span style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
                · {w.relationship} · added by {w.added_by}
              </span>
            </div>
            <div style={{ fontSize: "1.1rem", color: "var(--text)", lineHeight: 1.75, marginBottom: "10px" }}>
              "{w.text}"
            </div>
            {w.occasion && (
              <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "8px" }}>— {w.occasion}</div>
            )}
            {w.audio_url && (
              <button onClick={() => togglePlay(w.id, w.audio_url)} style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                background: "rgba(232,131,42,0.1)", border: "1px solid rgba(232,131,42,0.3)",
                borderRadius: "20px", padding: "6px 14px",
                color: "var(--saffron)", fontSize: "0.95rem", cursor: "pointer", marginBottom: "8px",
              }}>
                {playing === w.id ? "⏸ Playing..." : "▶ Hear their voice"}
              </button>
            )}
            {w.gita_verse && (
              <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
                🕉️ Echoes {w.gita_verse} — {w.gita_reason}
              </div>
            )}
          </div>
        ))}
      </div>

      {showAdd && (
        <Overlay onClose={() => setShowAdd(false)}>
          <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--saffron)", marginBottom: "16px" }}>
            Add Ancestral Wisdom
          </div>
          <Label>Elder's name</Label>
          <input value={form.ancestor_name} onChange={e => setForm(f => ({...f, ancestor_name: e.target.value}))}
            placeholder="e.g. Thatha, Naana, Paati" style={inputStyle} />
          <Label style={{ marginTop: "12px" }}>Relationship</Label>
          <input value={form.relationship} onChange={e => setForm(f => ({...f, relationship: e.target.value}))}
            placeholder="e.g. Grandfather, Father" style={inputStyle} />
          <Label style={{ marginTop: "12px" }}>Their wisdom</Label>
          <textarea value={form.text} onChange={e => setForm(f => ({...f, text: e.target.value}))}
            placeholder="Their exact words..." rows={3}
            style={{ ...inputStyle, resize: "none", minHeight: "80px" }} />
          <Label style={{ marginTop: "12px" }}>When did they say this? (optional)</Label>
          <input value={form.occasion} onChange={e => setForm(f => ({...f, occasion: e.target.value}))}
            placeholder="e.g. Before my wedding, When I failed my exam" style={inputStyle} />
          <div style={{ marginTop: "14px", display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            {!audioURL ? (
              <button onClick={recording ? stopRecording : startRecording} style={{
                padding: "8px 16px", borderRadius: "8px", cursor: "pointer",
                background: recording ? "rgba(239,68,68,0.1)" : "rgba(232,131,42,0.1)",
                border: `1px solid ${recording ? "#dc2626" : "rgba(232,131,42,0.3)"}`,
                color: recording ? "#dc2626" : "var(--saffron)", fontSize: "0.95rem",
              }}>
                {recording ? "⏹ Stop recording" : "🎙 Record their voice"}
              </button>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <audio src={audioURL} controls style={{ height: "32px" }} />
                <button onClick={() => { setAudioURL(""); setAudioBlob(null); }} style={{
                  background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer",
                }}>✕</button>
              </div>
            )}
          </div>
          <button onClick={handleSave} disabled={saving || !form.ancestor_name.trim() || !form.text.trim()} style={primaryBtn}>
            {saving ? "Saving & matching to Gita..." : "Save Wisdom →"}
          </button>
        </Overlay>
      )}
    </div>
  );
}

// ── Seva ──────────────────────────────────────────────────────────────────────
const SEVA_TYPES = [
  { id: "feed_children", icon: "🍱", label: "Anna Seva",     desc: "Feed children at an ashrama" },
  { id: "feed_cow",      icon: "🐄", label: "Go Seva",       desc: "Donate to a Goshala" },
  { id: "plant_tree",    icon: "🌿", label: "Vriksha Seva",  desc: "Plant a tree" },
  { id: "annadanam",     icon: "🍛", label: "Annadanam",     desc: "Temple free food service" },
];
// ── VedSanga Curated Temple & Charity Directory ───────────────────────────────
// Official donation websites only — UPI IDs change, websites are permanent
// Tagged by seva_types[] so they appear under the right seva category

const CURATED_DESTINATIONS = [
  // ── Annadanam / Feed Children ──────────────────────────────────────────────
  {
    name: "Tirumala Tirupati Devasthanams (TTD)",
    location: "Tirupati, Andhra Pradesh",
    note: "World's largest Hindu pilgrimage trust · Govt body · Feeds lakhs daily",
    badge: "✓ Govt Trust",
    url: "https://ttdevasthanams.ap.gov.in/home/dashboard",
    seva_types: ["annadanam", "feed_children"],
  },
  {
    name: "Shirdi Sai Baba Sansthan Trust",
    location: "Shirdi, Maharashtra",
    note: "Official trust · Feeds 50,000+ pilgrims daily · 80G exemption",
    badge: "✓ Govt Trust",
    url: "https://www.sai.org.in/en/content/donation",
    seva_types: ["annadanam", "feed_children"],
  },
  {
    name: "Akshaya Patra Foundation",
    location: "Pan India",
    note: "55 lakh school children fed daily · FCRA · 80G · Publicly audited",
    badge: "✓ FCRA NGO",
    url: "https://www.akshayapatra.org/onlinedonations",
    seva_types: ["feed_children", "annadanam"],
  },
  {
    name: "Annamrita Foundation (ISKCON)",
    location: "Pan India",
    note: "Free mid-day meals at government schools · 80G registered",
    badge: "✓ FCRA NGO",
    url: "https://annamrita.org/annamrita-karuna/",
    seva_types: ["feed_children", "annadanam"],
  },
  {
    name: "Kashi Vishwanath Temple Trust",
    location: "Varanasi, Uttar Pradesh",
    note: "Official temple trust · Annadanam seva available",
    badge: "✓ Govt Trust",
    url: "https://www.shrikashivishwanath.org/donation",
    seva_types: ["annadanam"],
  },
  {
    name: "Somnath Temple Trust",
    location: "Somnath, Gujarat",
    note: "Shri Somnath Trust · Official donations accepted online",
    badge: "✓ Govt Trust",
    url: "https://somnath.org/online-donation/",
    seva_types: ["annadanam"],
  },
  {
    name: "Siddhivinayak Temple Trust",
    location: "Mumbai, Maharashtra",
    note: "Official Shri Siddhivinayak Ganapati Mandir · Online donations",
    badge: "✓ Govt Trust",
    url: "https://siddhivinayak.org/donation",
    seva_types: ["annadanam"],
  },
  {
    name: "Meenakshi Amman Temple",
    location: "Madurai, Tamil Nadu",
    note: "Arulmigu Meenakshi Sundareswarar Temple · HR&CE Dept.",
    badge: "✓ Govt Temple",
    url: "https://maduraimeenakshi.hrce.tn.gov.in/",
    seva_types: ["annadanam"],
  },
  {
    name: "ISKCON Food for Life",
    location: "Pan India",
    note: "Free prasadam worldwide · Largest vegan food relief",
    badge: "✓ FCRA NGO",
    url: "https://iskconfoodrelief.com/",
    seva_types: ["annadanam", "feed_children"],
  },

  // ── Go Seva (Cows) ─────────────────────────────────────────────────────────
  {
    name: "Pathmeda Godham",
    location: "Sanchore, Rajasthan",
    note: "Largest goshala in India · 1 lakh+ cows · Registered",
    badge: "✓ Registered",
    url: "https://www.godhampathmeda.org/donate-as-guest",
    seva_types: ["feed_cow"],
  },
  {
    name: "ISKCON Goshala Vrindavan",
    location: "Vrindavan, UP",
    note: "Sri Krishna's own land · Cow protection seva",
    badge: "✓ Registered",
    url: "https://iskconvrindavan.com/donate",
    seva_types: ["feed_cow"],
  },
  {
    name: "Guruvayur Devaswom (Elephant & Cow Seva)",
    location: "Guruvayur, Kerala",
    note: "Famous Guruvayurappan temple · Devaswom Board · Gajapooja",
    badge: "✓ Devaswom",
    url: "https://guruvayurdevaswom.in/#/login",
    seva_types: ["feed_cow"],
  },

  // ── Plant Tree ─────────────────────────────────────────────────────────────
  {
    name: "SankalpTaru Foundation",
    location: "Pan India",
    note: "Plant in your name · Get GPS location + certificate",
    badge: "✓ NGO",
    url: "https://www.sankalptaru.org",
    seva_types: ["plant_tree"],
  },
  {
    name: "Green Yatra",
    location: "Pan India",
    note: "Urban tree plantation · Multiple cities",
    badge: "✓ NGO",
    url: "https://www.greenyatra.org/donate.php",
    seva_types: ["plant_tree"],
  },

  // ── Vaishno Devi / Pilgrim temples ────────────────────────────────────────
  {
    name: "Shri Mata Vaishno Devi Shrine Board",
    location: "Katra, Jammu",
    note: "Official J&K Shrine Board · Online donations accepted",
    badge: "✓ Govt Board",
    url: "https://online.maavaishnodevi.org/#/login",
    seva_types: ["annadanam"],
  },
  {
    name: "Sringeri Sharada Peetham",
    location: "Sringeri, Karnataka",
    note: "Adi Shankaracharya's own peetham · 1200 years old",
    badge: "✓ Matham",
    url: "https://donate.sringeri.net/online-services",
    seva_types: ["annadanam"],
  },
];

// Group by seva type for easy lookup
const CURATED_BY_TYPE = SEVA_TYPES.reduce((acc, s) => {
  acc[s.id] = CURATED_DESTINATIONS.filter(d => d.seva_types.includes(s.id));
  return acc;
}, {});

function SevaSection({ circleId, circleName, user, recentSeva, mySevaToday, onRefresh, isAdmin }) {
  const [showModal,   setShowModal]   = useState(false);
  const [sevaType,    setSevaType]    = useState(null);
  const [step,        setStep]        = useState("pick"); // pick → choose → confirm
  const [selectedOrg, setSelectedOrg] = useState(null);  // { name, upi_id, isLocal }
  const [note,        setNote]        = useState("");
  const [submitting,  setSubmitting]  = useState(false);
  const [showCard,    setShowCard]    = useState(false);
  const [lastSeva,    setLastSeva]    = useState(null);
  const [copied,      setCopied]      = useState(false);
  const [localDests,  setLocalDests]  = useState([]);
  const [showAddDest, setShowAddDest] = useState(false);
  const [newDest,     setNewDest]     = useState({ name: "", upi_id: "", note: "" });
  const [savingDest,  setSavingDest]  = useState(false);

  const loadDests = async () => {
    try {
      const r = await fetch(`${API}/circles/${circleId}/seva-destinations?user_id=${encodeURIComponent(user.id)}`);
      const d = await r.json();
      setLocalDests(d.destinations || []);
    } catch {}
  };

  const openModal = () => {
    setStep("pick"); setSevaType(null); setSelectedOrg(null); setNote("");
    loadDests(); setShowModal(true);
  };

  const goToChoose = () => { if (sevaType) setStep("choose"); };

  const selectOrg = (org) => { setSelectedOrg(org); setStep("confirm"); };

  const openDonate = () => {
    if (!selectedOrg) return;
    if (selectedOrg.url) { window.open(selectedOrg.url, "_blank"); return; }
    const upiLink = `upi://pay?pa=${encodeURIComponent(selectedOrg.upi_id)}&pn=${encodeURIComponent(selectedOrg.name)}&cu=INR&tn=VedSanga+Seva`;
    window.open(upiLink, "_blank");
  };

  const confirm = async () => {
    setSubmitting(true);
    await fetch(`${API}/circles/${circleId}/seva`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user.id, display_name: user.name, seva_type: sevaType, amount: 0, note: note.trim() }),
    });
    const seva = SEVA_TYPES.find(s => s.id === sevaType);
    setLastSeva({ seva, org: selectedOrg, note: note.trim(), circleName });
    setNote(""); setSubmitting(false); setShowModal(false); setShowCard(true);
    await onRefresh();
  };

  const saveNewDest = async () => {
    if (!newDest.name.trim() || !newDest.upi_id.trim() || !sevaType) return;
    setSavingDest(true);
    await fetch(`${API}/circles/${circleId}/seva-destination`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: user.id, seva_type: sevaType, name: newDest.name.trim(), upi_id: newDest.upi_id.trim(), note: newDest.note.trim() }),
    });
    setNewDest({ name: "", upi_id: "", note: "" });
    setSavingDest(false); setShowAddDest(false);
    await loadDests();
  };

  const shareText = lastSeva ? [
    `🌾 *${lastSeva.circleName}* performed seva today`,
    ``,
    `${lastSeva.seva.icon} *${lastSeva.seva.label}*`,
    lastSeva.note ? `_"${lastSeva.note}"_` : "",
    ``,
    `_"Perform your duty without attachment to the fruits."_`,
    `— Bhagavad Gita 3.8`,
    ``,
    `Read the Gita with your family every morning 🕉️`,
    `Download VedSanga → vedsanga.com`,
  ].filter(l => l !== null).join("\n") : "";

  const handleShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ text: shareText }); return; } catch {}
    }
    await navigator.clipboard.writeText(shareText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: "16px", padding: "22px", marginBottom: "20px", position: "relative", overflow: "hidden", boxShadow: "var(--card-shadow)" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,var(--saffron),var(--gold))" }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
        <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--gold)" }}>
          🌾 Family seva
        </div>
        <button onClick={openModal} style={{
          padding: "7px 16px", borderRadius: "8px",
          background: "var(--saffron)",
          border: "none", color: "white",
          fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem",
          cursor: "pointer",
        }}>
          + Record Seva
        </button>
      </div>

      <div style={{ fontSize: "1rem", color: "var(--text)", lineHeight: 1.6, marginBottom: recentSeva.length > 0 ? "14px" : 0 }}>
        Money goes directly to the charity — VedSanga never touches it.
      </div>

      {recentSeva.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {recentSeva.slice(0, 3).map((s, i) => {
            const st = SEVA_TYPES.find(t => t.id === s.seva_type);
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "1rem" }}>
                <span style={{ fontSize: "1.2rem" }}>{st?.icon || "🙏"}</span>
                <span style={{ color: "var(--text)" }}>{s.name}</span>
                <span style={{ color: "var(--text-dim)" }}>· {st?.label}</span>
                <span style={{ marginLeft: "auto", fontSize: "0.95rem", color: "var(--text-dim)" }}>{timeAgo(s.recorded_at)}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Seva modal */}
      {showModal && (
        <Overlay onClose={() => setShowModal(false)}>

          {/* Step 1 — pick seva type */}
          {step === "pick" && <>
            <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--saffron)", marginBottom: "4px" }}>Choose Seva</div>
            <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "16px" }}>What act of service did your family perform?</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "18px" }}>
              {SEVA_TYPES.map(s => (
                <div key={s.id} onClick={() => setSevaType(s.id)} style={{
                  padding: "14px 10px", borderRadius: "10px", textAlign: "center",
                  background: sevaType === s.id ? "rgba(232,131,42,0.12)" : "var(--bg3)",
                  border: `1px solid ${sevaType === s.id ? "var(--saffron)" : "var(--border)"}`,
                  cursor: "pointer", transition: "all 0.15s",
                }}>
                  <div style={{ fontSize: "2rem", marginBottom: "8px" }}>{s.icon}</div>
                  <div style={{ fontSize: "1rem", color: sevaType === s.id ? "var(--saffron)" : "var(--cream)", fontFamily: "'Inter',sans-serif", fontWeight: 700 }}>{s.label}</div>
                  <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginTop: "4px", lineHeight: 1.4 }}>{s.desc}</div>
                </div>
              ))}
            </div>
            <button onClick={goToChoose} disabled={!sevaType} style={{
              ...primaryBtn, marginTop: 0,
              background: sevaType ? "var(--saffron)" : "var(--bg3)",
              color: sevaType ? "white" : "var(--text-dim)",
              cursor: sevaType ? "pointer" : "not-allowed",
            }}>Next →</button>
          </>}

          {/* Step 2 — choose organisation */}
          {step === "choose" && (() => {
            const curated = CURATED_BY_TYPE[sevaType] || [];
            const myLocal = localDests.filter(d => d.seva_type === sevaType);
            const [search, setSearch] = [
              // inline search state via useRef trick — simple filter
              "", () => {}
            ];
            return <>
              <button onClick={() => setStep("pick")} style={{ background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer", fontSize: "0.95rem", padding: 0, marginBottom: "10px" }}>← Back</button>
              <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--saffron)", marginBottom: "4px" }}>Where to donate?</div>
              <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "14px" }}>Money goes directly to the temple/charity — VedSanga never receives it.</div>

              {/* Scrollable list */}
              <div style={{ maxHeight: "340px", overflowY: "auto", marginBottom: "10px" }}>

                {/* VedSanga verified — curated temples */}
                {curated.length > 0 && (
                  <>
                    <div style={{ fontSize: "0.95rem", color: "var(--gold)", marginBottom: "10px", display: "flex", alignItems: "center", gap: "6px", fontFamily: "'Inter',sans-serif", fontWeight: 700 }}>
                      🕉 VedSanga verified
                    </div>
                    {curated.map((org, i) => (
                      <div key={i} onClick={() => selectOrg({ ...org, isLocal: false })} style={{
                        background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: "12px",
                        padding: "14px 16px", marginBottom: "10px", cursor: "pointer", transition: "border-color 0.15s",
                      }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = "var(--saffron)"}
                        onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div style={{ fontSize: "1rem", color: "var(--cream)", flex: 1, fontWeight: 600 }}>{org.name}</div>
                          <div style={{ fontSize: "0.9rem", color: "#15803d", flexShrink: 0, marginLeft: "8px" }}>{org.badge}</div>
                        </div>
                        <div style={{ fontSize: "0.95rem", color: "#a16207", marginTop: "4px" }}>{org.location}</div>
                        <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginTop: "5px", lineHeight: 1.5 }}>{org.note}</div>
                        <div style={{ fontSize: "0.95rem", color: "var(--saffron)", marginTop: "8px" }}>Donate on official website →</div>
                      </div>
                    ))}
                  </>
                )}

                {/* Admin-added local temples */}
                {myLocal.length > 0 && (
                  <>
                    <div style={{ fontSize: "0.95rem", color: "var(--gold)", margin: "14px 0 10px", fontFamily: "'Inter',sans-serif", fontWeight: 700 }}>
                      Your local temples & ashrams
                    </div>
                    {myLocal.map(d => (
                      <div key={d.id} onClick={() => selectOrg({ name: d.name, upi_id: d.upi_id, note: d.note, location: "Your circle", badge: "Local", isLocal: true })} style={{
                        background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: "12px",
                        padding: "14px 16px", marginBottom: "10px", cursor: "pointer", transition: "border-color 0.15s",
                      }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = "var(--saffron)"}
                        onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}
                      >
                        <div style={{ fontSize: "1rem", color: "var(--cream)", fontWeight: 600 }}>{d.name}</div>
                        {d.note && <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginTop: "4px" }}>{d.note}</div>}
                        <div style={{ fontSize: "0.95rem", color: "#a16207", marginTop: "6px" }}>Pay via UPI →</div>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* Admin: add local temple */}
              {isAdmin && !showAddDest && (
                <button onClick={() => setShowAddDest(true)} style={{
                  width: "100%", padding: "9px", borderRadius: "8px",
                  background: "transparent", border: "1px dashed var(--border)",
                  color: "var(--text-dim)", fontSize: "0.95rem", cursor: "pointer",
                }}>
                  + Add your local temple / ashram
                </button>
              )}
              {isAdmin && showAddDest && (
                <div style={{ background: "var(--bg3)", borderRadius: "10px", padding: "14px" }}>
                  <div style={{ fontSize: "0.95rem", color: "var(--gold)", fontWeight: 700, marginBottom: "10px" }}>Add local temple / ashram</div>
                  <input value={newDest.name} onChange={e => setNewDest(d => ({...d, name: e.target.value}))}
                    placeholder="Temple / ashram name" style={{ ...inputStyle, marginBottom: "8px" }} />
                  <input value={newDest.upi_id} onChange={e => setNewDest(d => ({...d, upi_id: e.target.value}))}
                    placeholder="UPI ID (e.g. templename@sbi)" style={{ ...inputStyle, marginBottom: "8px" }} />
                  <input value={newDest.note} onChange={e => setNewDest(d => ({...d, note: e.target.value}))}
                    placeholder="Short note (optional)" style={{ ...inputStyle, marginBottom: "10px" }} />
                  <button onClick={saveNewDest} disabled={savingDest || !newDest.name.trim() || !newDest.upi_id.trim()} style={{ ...primaryBtn, marginTop: 0, fontSize: "0.95rem" }}>
                    {savingDest ? "Saving..." : "Save →"}
                  </button>
                </div>
              )}
            </>;
          })()}

          {/* Step 3 — confirm after paying */}
          {step === "confirm" && selectedOrg && <>
            <button onClick={() => setStep("choose")} style={{ background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer", fontSize: "0.95rem", padding: 0, marginBottom: "12px" }}>← Back</button>
            <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--saffron)", marginBottom: "16px" }}>Complete Your Seva</div>

            <div style={{ background: "rgba(232,131,42,0.06)", border: "1px solid rgba(232,131,42,0.3)", borderRadius: "12px", padding: "14px", marginBottom: "16px" }}>
              <div style={{ fontSize: "1rem", color: "var(--cream)", marginBottom: "4px" }}>{selectedOrg.name}</div>
              {selectedOrg.note && <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>{selectedOrg.note}</div>}
            </div>

            <button onClick={openDonate} style={{
              width: "100%", padding: "12px", borderRadius: "10px", marginBottom: "14px",
              background: "var(--saffron)",
              border: "none", color: "white",
              fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem", cursor: "pointer",
            }}>
              {selectedOrg.isLocal ? "🙏 Open UPI →" : "🙏 Donate on their website →"}
            </button>

            <input value={note} onChange={e => setNote(e.target.value)}
              placeholder="Note (optional) — e.g. In memory of our grandfather"
              style={{ ...inputStyle, marginBottom: "12px" }} />

            <button onClick={confirm} disabled={submitting} style={{
              ...primaryBtn, marginTop: 0,
              background: "rgba(232,131,42,0.15)", border: "1px solid rgba(232,131,42,0.4)", color: "var(--saffron)",
            }}>
              {submitting ? "Recording..." : "✓ I completed this seva"}
            </button>
            <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", textAlign: "center", marginTop: "8px" }}>
              Tap after donating. Between you and God.
            </div>
          </>}
        </Overlay>
      )}

      {/* Shareable seva card */}
      {showCard && lastSeva && (
        <Overlay onClose={() => setShowCard(false)}>
          <div style={{
            background: "var(--bg2)",
            border: "1px solid var(--border)",
            borderRadius: "16px", overflow: "hidden", marginBottom: "16px",
            boxShadow: "var(--card-shadow)",
          }}>
            <div style={{ height: "3px", background: "linear-gradient(90deg,var(--saffron),var(--gold),var(--saffron))" }} />
            <div style={{ padding: "28px 24px", textAlign: "center" }}>
              <div style={{ fontSize: "2.6rem", marginBottom: "10px", filter: "drop-shadow(0 0 12px rgba(232,131,42,0.5))" }}>
                {lastSeva.seva.icon}
              </div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem", color: "var(--gold)", marginBottom: "4px" }}>
                {lastSeva.circleName}
              </div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.15rem", color: "var(--text)", marginBottom: "6px" }}>
                performed seva today
              </div>
              <div style={{ fontSize: "1.05rem", color: "var(--saffron)", marginBottom: lastSeva.note ? "10px" : "20px", fontFamily: "'Inter',sans-serif", fontWeight: 700 }}>
                {lastSeva.seva.label}
              </div>
              {lastSeva.note && (
                <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "20px" }}>
                  "{lastSeva.note}"
                </div>
              )}
              <div style={{ borderTop: "1px solid var(--border)", paddingTop: "16px", fontStyle: "italic", color: "var(--text-dim)", fontSize: "0.95rem", lineHeight: 1.8 }}>
                "Perform your duty without attachment to the fruits."
                <div style={{ color: "var(--text-dim)", fontSize: "0.9rem", marginTop: "4px" }}>— Bhagavad Gita 3.8</div>
              </div>
            </div>
            <div style={{ padding: "10px 24px", textAlign: "center", background: "var(--bg3)", borderTop: "1px solid var(--border)" }}>
              <span style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.9rem", color: "var(--gold)" }}>
                🕉 VedSanga
              </span>
            </div>
          </div>

          <button onClick={handleShare} style={{
            width: "100%", padding: "12px", borderRadius: "10px",
            background: "#ffffff",
            border: `1px solid ${copied ? "#065f46" : "#047857"}`,
            color: copied ? "#065f46" : "#047857",
            fontFamily: "'Inter',sans-serif", fontWeight: 600, fontSize: "0.95rem",
            cursor: "pointer",
          }}>
            {copied ? "✓ Copied to clipboard!" : "📱 Share with family on WhatsApp"}
          </button>
        </Overlay>
      )}
    </div>
  );
}

// Weekly timetable — what today's focus is
function weeklyFocus() {
  const day = new Date().getDay(); // 0=Sun,1=Mon,...,6=Sat
  const schedule = {
    0: { label: "Sunday Gratitude", prompt: "Share one thing your family is grateful for this week 🙏", icon: "🌸" },
    1: { label: "Monday Shloka", prompt: "Read today's verse slowly — twice. Let it settle.", icon: "📖" },
    2: { label: "Tuesday Reflection", prompt: "How did yesterday's verse show up in your day?", icon: "🪷" },
    3: { label: "Wednesday Discussion", prompt: "What does this verse mean to your family right now?", icon: "💬" },
    4: { label: "Thursday Practice", prompt: "One small action today inspired by this week's verse.", icon: "🌿" },
    5: { label: "Friday Lakshmi Day", prompt: "Light a lamp at home. Share what you are inviting into your family.", icon: "🪔" },
    6: { label: "Saturday Sankalpa", prompt: "What is your family's intention for next week?", icon: "✨" },
  };
  return schedule[day];
}

function CircleView({ circleId, user, onBack }) {
  const [data,          setData]          = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [acking,        setAcking]        = useState(false);
  const [lighting,      setLighting]      = useState(false);
  const [question,      setQuestion]      = useState("");
  const [asking,        setAsking]        = useState(false);
  const [showAsk,       setShowAsk]       = useState(false);
  const [showSankalpa,  setShowSankalpa]  = useState(false);
  const [sankalpaTxt,   setSankalpaTxt]   = useState("");
  const [savingSank,    setSavingSank]    = useState(false);
  const [showMilestone, setShowMilestone] = useState(null);
  const [showInvite,    setShowInvite]    = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const res = await fetch(`${API}/circles/${circleId}/today/${encodeURIComponent(user.id)}`);
      const d   = await res.json();
      setData(d);
    } catch {}
    setLoading(false);
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(pollRef.current);
  }, [circleId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAcknowledge = async () => {
    if (acking || data?.acknowledged_today) return;
    setAcking(true);
    await fetch(`${API}/circles/${circleId}/acknowledge?user_id=${encodeURIComponent(user.id)}`, { method: "POST" });
    await load();
    setAcking(false);
  };

  const handleAsk = async () => {
    if (!question.trim() || asking) return;
    setAsking(true);
    try {
      const res = await fetch(`${API}/circles/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ circle_id: circleId, user_id: user.id, question: question.trim() }),
      });
      const d = await res.json();
      if (d.answer) {
        setQuestion("");
        setShowAsk(false);
        await load();
      }
    } catch {}
    setAsking(false);
  };

  const handleLightLamp = async () => {
    if (lighting || data?.lit_today) return;
    setLighting(true);
    await fetch(`${API}/circles/${circleId}/puja-bell?user_id=${encodeURIComponent(user.id)}`, { method: "POST" });
    await load();
    setLighting(false);
  };

  const handleSankalpa = async () => {
    if (!sankalpaTxt.trim() || savingSank) return;
    setSavingSank(true);
    await fetch(`${API}/circles/${circleId}/sankalpa`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text: sankalpaTxt.trim(), user_id: user.id }),
    });
    setSankalpaTxt("");
    setShowSankalpa(false);
    setSavingSank(false);
    await load();
  };

  if (loading) return (
    <div style={{ color: "var(--text-dim)", padding: "60px", textAlign: "center" }}>
      🕉️ Loading circle...
    </div>
  );

  if (!data || data.error) return (
    <div style={{ color: "var(--text-dim)", padding: "40px" }}>Circle not found.</div>
  );

  const { verse, members, acknowledged_today, questions, circle_name,
          puja_members, lit_today, streak, sankalpa, is_admin,
          full_days, milestones_earned, next_milestone, festival,
          todays_leader, recent_seva = [], my_seva_today = false } = data;
  const isLeader = todays_leader?.user_id === user.id;
  const acknowledged_count = members.filter(m => m.acknowledged).length;
  const lit_count = (puja_members || []).filter(m => m.lit).length;
  const focus = weeklyFocus();

  const copyInvite = () => {
    setShowInvite(true);
  };

  return (
    <div>
      {/* Back + header */}
      <button onClick={onBack} style={{
        background: "none", border: "none", cursor: "pointer",
        color: "var(--text-dim)", fontFamily: "'Inter',sans-serif",
        fontSize: "1rem", padding: "0", marginBottom: "20px",
        display: "flex", alignItems: "center", gap: "6px",
      }}>
        ← All Circles
      </button>

      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "12px",
      }}>
        <div>
          <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.3rem", color: "var(--saffron)" }}>
            {circle_name}
          </div>
          <div style={{ fontSize: "1rem", color: "var(--text-dim)", marginTop: "2px", display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <span>{members.length} member{members.length !== 1 ? "s" : ""} · {acknowledged_count} read today</span>
            {streak > 0 && (
              <span style={{
                background: streak >= 7 ? "rgba(232,131,42,0.15)" : "var(--bg3)",
                border: `1px solid ${streak >= 7 ? "var(--saffron)" : "var(--border)"}`,
                borderRadius: "20px", padding: "2px 10px",
                fontSize: "0.95rem", color: streak >= 7 ? "var(--saffron)" : "var(--text-dim)",
              }}>
                🔥 {streak} day streak
              </span>
            )}
          </div>
        </div>
        <button onClick={() => setShowAsk(true)} style={{
          padding: "8px 18px", borderRadius: "8px",
          background: "#ffffff",
          border: "1px solid var(--saffron)",
          color: "var(--saffron)", fontFamily: "'Inter',sans-serif", fontWeight: 600,
          fontSize: "0.95rem", cursor: "pointer",
        }}>
          Ask the Gita
        </button>
      </div>

      {/* Festival Banner */}
      {festival && (
        <div style={{
          background: "linear-gradient(135deg,rgba(201,149,58,0.15),rgba(232,131,42,0.08))",
          border: "1px solid var(--gold)",
          borderRadius: "14px", padding: "20px", marginBottom: "20px",
        }}>
          <div style={{ textAlign: "center", marginBottom: "16px" }}>
            <div style={{ fontSize: "1.8rem", marginBottom: "8px" }}>🪔</div>
            <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--gold)" }}>
              {festival.name}
            </div>
            <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginTop: "4px" }}>
              {festival.deity}
            </div>
          </div>

          {festival.story && (
            <div style={{ fontSize: "0.95rem", color: "var(--text)", lineHeight: 1.7, marginBottom: "14px" }}>
              {festival.story}
            </div>
          )}

          {festival.shloka && (
            <div style={{
              borderLeft: "2px solid var(--gold)", paddingLeft: "12px",
              fontStyle: "italic", color: "var(--cream)",
              fontSize: "1rem", lineHeight: 1.7, marginBottom: "14px",
            }}>
              {festival.shloka}
            </div>
          )}

          {festival.family_practice && (
            <div style={{
              background: "rgba(232,131,42,0.1)", borderRadius: "8px",
              padding: "10px 14px", fontSize: "0.95rem",
              color: "var(--saffron)",
            }}>
              🙏 <strong>Today's family practice:</strong> {festival.family_practice}
            </div>
          )}
        </div>
      )}

      {/* Today's verse */}
      <div style={{
        background: "linear-gradient(135deg,rgba(232,131,42,0.08),rgba(201,149,58,0.04))",
        border: "1px solid rgba(232,131,42,0.3)",
        borderRadius: "16px", padding: "24px",
        marginBottom: "20px",
      }}>
        <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", fontWeight: 700, marginBottom: "12px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "8px" }}>
          <span>Today's verse — BG {verse.chapter}.{verse.verse}</span>
          {todays_leader && (
            <span style={{
              background: isLeader ? "var(--saffron)" : "rgba(232,131,42,0.1)",
              border: "1px solid rgba(232,131,42,0.35)",
              borderRadius: "20px", padding: "3px 10px",
              fontSize: "0.95rem",
              color: isLeader ? "white" : "var(--saffron)",
            }}>
              {isLeader ? "🌟 You lead today" : `🌟 ${todays_leader.name} leads today`}
            </span>
          )}
        </div>

        {verse.sanskrit && (
          <div style={{
            fontFamily: "'Noto Sans Devanagari',serif",
            fontSize: "1.15rem", color: "var(--cream)",
            lineHeight: 1.8, marginBottom: "10px",
            textShadow: "0 0 20px rgba(232,131,42,0.3)",
          }}>
            {verse.sanskrit}
          </div>
        )}

        <div style={{
          fontSize: "1.05rem", color: "var(--text)",
          lineHeight: 1.8, marginBottom: "20px",
        }}>
          {verse.meaning}
        </div>

        {/* Acknowledge button */}
        <button
          onClick={handleAcknowledge}
          disabled={acknowledged_today || acking}
          style={{
            display: "flex", alignItems: "center", gap: "8px",
            padding: "10px 24px", borderRadius: "10px",
            background: acknowledged_today
              ? "rgba(232,131,42,0.2)"
              : "var(--saffron)",
            border: acknowledged_today ? "1px solid var(--saffron)" : "none",
            color: acknowledged_today ? "var(--saffron)" : "white",
            fontFamily: "'Inter',sans-serif", fontWeight: 600, fontSize: "1.05rem",
            cursor: acknowledged_today ? "default" : "pointer",
            transition: "all 0.2s",
          }}
        >
          {acknowledged_today ? "🙏 Read today" : acking ? "..." : "🙏 I read this"}
        </button>
      </div>

      {/* Members grid */}
      <div style={{
        background: "var(--bg2)", border: "1px solid var(--border)",
        borderRadius: "14px", padding: "20px", marginBottom: "20px",
        position: "relative", overflow: "hidden", boxShadow: "var(--card-shadow)",
      }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,var(--saffron),var(--gold),transparent)" }} />
        <div style={{
          fontFamily: "'Inter',sans-serif", fontWeight: 700,
          fontSize: "1.05rem", color: "var(--gold)",
          marginBottom: "16px",
        }}>
          Today's circle
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
          {members.map(m => (
            <div key={m.user_id} style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              gap: "6px", minWidth: "64px",
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: "50%",
                background: m.acknowledged
                  ? "linear-gradient(135deg, var(--saffron), var(--gold))"
                  : "var(--bg3)",
                border: m.acknowledged
                  ? "2px solid var(--saffron)"
                  : "2px solid var(--border)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1.2rem",
                transition: "all 0.3s",
              }}>
                {m.acknowledged ? "🙏" : m.name[0]?.toUpperCase() || "?"}
              </div>
              <div style={{
                fontSize: "0.95rem",
                color: m.acknowledged ? "var(--saffron)" : "var(--text-dim)",
                textAlign: "center", maxWidth: "64px",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}>
                {m.name}
              </div>
            </div>
          ))}
        </div>

        {acknowledged_count === members.length && members.length > 1 && (
          <div style={{
            marginTop: "16px", padding: "10px 16px",
            background: "#ffffff",
            border: "1px solid #bbf7d0",
            borderRadius: "8px", color: "#15803d",
            fontSize: "0.95rem",
          }}>
            Everyone in your circle read today's verse. 🙏
          </div>
        )}
      </div>

      {/* Daily Puja Bell */}
      <div style={{
        background: "var(--bg2)", border: "1px solid var(--border)",
        borderRadius: "14px", padding: "20px", marginBottom: "20px",
        position: "relative", overflow: "hidden", boxShadow: "var(--card-shadow)",
      }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,var(--gold),var(--saffron),transparent)" }} />
        <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--gold)", marginBottom: "16px" }}>
          🪔 Daily puja bell
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "16px" }}>
          {(puja_members || []).map((m, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", minWidth: "56px" }}>
              <div style={{
                width: 44, height: 44, borderRadius: "50%",
                background: m.lit ? "linear-gradient(135deg,var(--gold),var(--saffron))" : "var(--bg3)",
                border: `2px solid ${m.lit ? "var(--gold)" : "var(--border)"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1.1rem", transition: "all 0.3s",
              }}>
                {m.lit ? "🪔" : m.name[0]?.toUpperCase() || "?"}
              </div>
              <div style={{ fontSize: "0.95rem", color: m.lit ? "#a16207" : "var(--text-dim)", textAlign: "center", maxWidth: "56px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {m.name}
              </div>
            </div>
          ))}
        </div>
        <button
          onClick={handleLightLamp}
          disabled={lit_today || lighting}
          style={{
            display: "flex", alignItems: "center", gap: "8px",
            padding: "9px 22px", borderRadius: "10px",
            background: lit_today ? "rgba(201,149,58,0.15)" : "var(--gold)",
            border: lit_today ? "1px solid var(--gold)" : "none",
            color: lit_today ? "#a16207" : "white",
            fontFamily: "'Inter',sans-serif", fontWeight: 600, fontSize: "1rem",
            cursor: lit_today ? "default" : "pointer", transition: "all 0.2s",
          }}
        >
          {lit_today ? "🪔 Lamp lit today" : lighting ? "..." : "🪔 I lit the lamp"}
        </button>
        {!lit_today && lit_count > 0 && (
          <div style={{ marginTop: "12px", fontSize: "0.95rem", color: "var(--text-dim)" }}>
            {(puja_members || []).filter(m => m.lit && m.user_id !== user.id).map(m => m.name).join(" and ")}
            {" "}{lit_count === 1 ? "has" : "have"} lit the lamp. Waiting for you 🙏
          </div>
        )}
        {lit_count === (puja_members || []).length && (puja_members || []).length > 1 && (
          <div style={{ marginTop: "12px", fontSize: "0.95rem", color: "#a16207" }}>
            Every family member lit their lamp today. ✨
          </div>
        )}
      </div>

      {/* Weekly Focus */}
      <div style={{
        background: "rgba(232,131,42,0.06)", border: "1px dashed rgba(232,131,42,0.3)",
        borderRadius: "14px", padding: "18px 20px", marginBottom: "20px",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
          <span style={{ fontSize: "1.4rem" }}>{focus.icon}</span>
          <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1rem", color: "var(--saffron)" }}>
            {focus.label}
          </div>
        </div>
        <div style={{ fontSize: "1.1rem", color: "var(--text)", lineHeight: 1.6 }}>
          {focus.prompt}
        </div>
      </div>

      {/* Sacred Milestones */}
      <div style={{
        background: "var(--bg2)", border: "1px solid var(--border)",
        borderRadius: "14px", padding: "20px", marginBottom: "20px",
        position: "relative", overflow: "hidden", boxShadow: "var(--card-shadow)",
      }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,var(--gold),var(--saffron),transparent)" }} />
        <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--gold)", marginBottom: "16px" }}>
          🏅 Sacred milestones
        </div>

        {/* Earned milestones */}
        {(milestones_earned || []).length > 0 && (
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "16px" }}>
            {milestones_earned.map(m => (
              <button key={m.days} onClick={() => setShowMilestone(m)} style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "8px 14px", borderRadius: "20px",
                background: "rgba(232,131,42,0.12)",
                border: "1px solid var(--saffron)",
                color: "var(--saffron)", fontFamily: "'Inter',sans-serif", fontWeight: 600,
                fontSize: "0.95rem", cursor: "pointer",
              }}>
                {m.emoji} {m.name}
              </button>
            ))}
          </div>
        )}

        {/* Progress to next milestone */}
        {next_milestone && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "6px" }}>
              <span>{full_days} full days together</span>
              <span>{next_milestone.emoji} {next_milestone.name} at {next_milestone.days} days</span>
            </div>
            <div style={{ background: "var(--bg3)", borderRadius: "6px", height: "6px", overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: "6px",
                background: "linear-gradient(90deg, var(--saffron), var(--gold))",
                width: `${Math.min(100, (full_days / next_milestone.days) * 100)}%`,
                transition: "width 0.5s ease",
              }} />
            </div>
            <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginTop: "6px" }}>
              {next_milestone.days - full_days} more days to {next_milestone.name}
            </div>
          </div>
        )}

        {!next_milestone && (milestones_earned || []).length > 0 && (
          <div style={{ fontSize: "0.95rem", color: "var(--saffron)" }}>
            Your family has reached the highest milestone. 🕉️
          </div>
        )}
      </div>

      {/* Family Sankalpa */}
      <div style={{
        background: "var(--bg2)", border: "1px solid var(--border)",
        borderRadius: "14px", padding: "20px", marginBottom: "20px",
        position: "relative", overflow: "hidden", boxShadow: "var(--card-shadow)",
      }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,var(--saffron),var(--gold),transparent)" }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
          <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--gold)" }}>
            ✨ This week's sankalpa
          </div>
          {is_admin && (
            <button onClick={() => setShowSankalpa(true)} style={{
              background: "none", border: "1px solid var(--border)", borderRadius: "6px",
              padding: "4px 10px", color: "var(--text-dim)", fontSize: "0.95rem", cursor: "pointer",
            }}>
              {sankalpa ? "Update" : "+ Set"}
            </button>
          )}
        </div>
        {sankalpa ? (
          <div style={{ fontSize: "1.05rem", color: "var(--text)", lineHeight: 1.7 }}>
            "{sankalpa.text}"
            <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginTop: "6px" }}>— {sankalpa.posted_by}</div>
          </div>
        ) : (
          <div style={{ fontSize: "1.05rem", color: "var(--text)" }}>
            {is_admin ? "Set this week's sankalpa for your family." : "No sankalpa set yet for this week."}
          </div>
        )}
      </div>

      {/* Recent Gita Q&A */}
      {questions.length > 0 && (
        <div style={{
          background: "var(--bg2)", border: "1px solid var(--border)",
          borderRadius: "16px", padding: "22px", marginBottom: "20px",
          position: "relative", overflow: "hidden", boxShadow: "var(--card-shadow)",
        }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,var(--saffron),var(--gold))" }} />
          <div style={{
            fontFamily: "'Inter',sans-serif", fontWeight: 700,
            fontSize: "1.05rem", color: "var(--gold)",
            marginBottom: "16px",
          }}>
            Questions to the Gita
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {questions.map(q => (
              <div key={q.id} style={{
                borderBottom: "1px solid var(--border)",
                paddingBottom: "16px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: "50%",
                    background: "rgba(232,131,42,0.15)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "0.95rem", color: "var(--saffron)", flexShrink: 0,
                  }}>
                    {q.asked_by[0]?.toUpperCase()}
                  </div>
                  <div style={{ fontSize: "0.95rem", color: "var(--text-dim)" }}>
                    <span style={{ color: "var(--text)" }}>{q.asked_by}</span> asked · {timeAgo(q.asked_at)}
                  </div>
                </div>
                <div style={{
                  color: "var(--text-dim)",
                  fontSize: "1.05rem", marginBottom: "10px",
                }}>
                  "{q.question}"
                </div>
                <div style={{
                  fontSize: "1.05rem", color: "var(--text)",
                  lineHeight: 1.75,
                  paddingLeft: "14px",
                  borderLeft: "2px solid var(--saffron)",
                }}>
                  {q.answer}
                  {q.chapter && (
                    <span style={{ display: "block", marginTop: "6px", fontSize: "0.95rem", color: "var(--text-dim)" }}>
                      — BG {q.chapter}.{q.verse}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Milestone card modal */}
      {showMilestone && (
        <MilestoneCard
          milestone={showMilestone}
          circleName={circle_name}
          onClose={() => setShowMilestone(null)}
        />
      )}

      {/* Ancestral Wisdom section */}
      <AncestralWisdom circleId={circleId} user={user} />

      {/* Family Seva */}
      <SevaSection
        circleId={circleId}
        circleName={circle_name}
        user={user}
        recentSeva={recent_seva}
        mySevaToday={my_seva_today}
        isAdmin={is_admin}
        onRefresh={load}
      />

      {/* Sankalpa modal */}
      {showSankalpa && (
        <Overlay onClose={() => setShowSankalpa(false)}>
          <div style={{ fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1.05rem", color: "var(--saffron)", marginBottom: "8px" }}>
            This Week's Sankalpa
          </div>
          <div style={{ fontSize: "0.95rem", color: "var(--text-dim)", marginBottom: "16px" }}>
            Set an intention for your family this week — spiritual or personal.
          </div>
          <textarea
            value={sankalpaTxt}
            onChange={e => setSankalpaTxt(e.target.value)}
            placeholder="e.g. We will wake at 6am for puja, or We will call Thatha every day"
            rows={3}
            style={{ ...inputStyle, resize: "none", minHeight: "80px" }}
          />
          <button onClick={handleSankalpa} disabled={savingSank || !sankalpaTxt.trim()} style={primaryBtn}>
            {savingSank ? "Saving..." : "Set Sankalpa →"}
          </button>
        </Overlay>
      )}

      {/* Ask modal */}
      {showAsk && (
        <Overlay onClose={() => setShowAsk(false)}>
          <div style={{
            fontFamily: "'Inter',sans-serif", fontWeight: 700,
            fontSize: "1.05rem", color: "var(--saffron)", marginBottom: "8px",
          }}>
            Ask the Gita
          </div>
          <div style={{
            fontSize: "0.95rem", color: "var(--text-dim)",
            marginBottom: "16px",
          }}>
            Your question and the Gita's answer will be visible to the whole circle.
          </div>
          <textarea
            value={question}
            onChange={e => setQuestion(e.target.value)}
            placeholder="What does the Gita say about letting go of the past?"
            rows={3}
            style={{
              ...inputStyle,
              resize: "none", lineHeight: 1.6,
              height: "auto", minHeight: "80px",
            }}
          />
          <button onClick={handleAsk} disabled={asking || !question.trim()} style={primaryBtn}>
            {asking ? "Asking..." : "Ask →"}
          </button>
        </Overlay>
      )}
    </div>
  );
}

// ── Circle list card ──────────────────────────────────────────────────────────
function CircleCard({ circle, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "var(--bg2)",
        border: `1px solid ${hovered ? "var(--saffron)" : "var(--border)"}`,
        borderRadius: "14px", padding: "20px 24px",
        cursor: "pointer", transition: "all 0.2s",
        display: "flex", alignItems: "center",
        justifyContent: "space-between", gap: "16px",
        boxShadow: hovered ? "var(--card-shadow-hover)" : "var(--card-shadow)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
        <div style={{
          width: 44, height: 44, borderRadius: "50%",
          background: "rgba(232,131,42,0.12)",
          border: "1px solid rgba(232,131,42,0.3)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "1.3rem", flexShrink: 0,
        }}>
          🕉️
        </div>
        <div>
          <div style={{
            fontFamily: "'Inter',sans-serif", fontWeight: 700,
            fontSize: "1.15rem", color: "var(--cream)",
          }}>
            {circle.name}
          </div>
          <div style={{ fontSize: "1rem", color: "var(--text-dim)", marginTop: "4px", fontFamily: "'Inter',sans-serif" }}>
            {circle.member_count} member{circle.member_count !== 1 ? "s" : ""}
            {circle.is_admin && " · Admin"}
          </div>
        </div>
      </div>
      <div style={{ color: "var(--text-dim)", fontSize: "1.2rem" }}>→</div>
    </div>
  );
}

// ── Shared UI primitives ──────────────────────────────────────────────────────
function Overlay({ onClose, children }) {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px",
    }} onClick={onClose}>
      <div style={{
        background: "var(--bg2)", border: "1px solid var(--border)",
        borderRadius: "20px", padding: "32px",
        width: "100%", maxWidth: "420px",
        position: "relative",
      }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} style={{
          position: "absolute", top: "16px", right: "16px",
          background: "none", border: "none",
          color: "var(--text-dim)", cursor: "pointer", fontSize: "1.2rem",
        }}>✕</button>
        {children}
      </div>
    </div>
  );
}

function Label({ children, style }) {
  return (
    <div style={{
      fontSize: "0.95rem", color: "var(--text-dim)",
      marginBottom: "8px",
      fontWeight: 700,
      fontFamily: "'Inter',sans-serif", ...style,
    }}>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", background: "var(--bg3)",
  border: "1px solid var(--border)", borderRadius: "8px",
  padding: "12px 16px", color: "var(--text)",
  fontFamily: "'Inter',sans-serif", fontSize: "1.05rem",
  outline: "none", boxSizing: "border-box",
};

const primaryBtn = {
  width: "100%", marginTop: "16px",
  padding: "13px", borderRadius: "10px",
  background: "var(--saffron)",
  border: "none", color: "white",
  fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "1rem",
  cursor: "pointer",
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN SatsangPage
// ══════════════════════════════════════════════════════════════════════════════
export default function SatsangPage({ user }) {
  const [circles,      setCircles]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [activeCircle, setActiveCircle] = useState(null);
  const [showCreate,   setShowCreate]   = useState(false);
  const [showJoin,     setShowJoin]     = useState(false);
  const [showCreated,  setShowCreated]  = useState(null); // newly created circle data

  const loadCircles = async () => {
    try {
      const res = await fetch(`${API}/circles/${encodeURIComponent(user.id)}`);
      const d   = await res.json();
      setCircles(d.circles || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadCircles(); }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div className="page">
      <div style={{ color: "var(--text-dim)", padding: "60px", textAlign: "center" }}>
        🕉️ Loading your circles...
      </div>
    </div>
  );

  if (activeCircle) {
    return (
      <div className="page">
        <CircleView
          circleId={activeCircle}
          user={user}
          onBack={() => setActiveCircle(null)}
        />
      </div>
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <div className="section-title">Satsang Circles</div>
          <div className="section-sub">A private space for family and close friends — read the Gita together</div>
        </div>
        {circles.length > 0 && (
          <div style={{ display: "flex", gap: "8px" }}>
            <button onClick={() => setShowJoin(true)} style={{
              padding: "8px 18px", borderRadius: "8px",
              background: "#ffffff", border: "1px solid var(--saffron)",
              color: "var(--saffron)", fontFamily: "'Inter',sans-serif", fontWeight: 600,
              fontSize: "1rem", cursor: "pointer",
            }}>
              Join
            </button>
            <button onClick={() => setShowCreate(true)} style={{
              padding: "8px 18px", borderRadius: "8px",
              background: "var(--saffron)",
              border: "none", color: "white",
              fontFamily: "'Inter',sans-serif", fontWeight: 700,
              fontSize: "0.95rem", cursor: "pointer",
            }}>
              + New
            </button>
          </div>
        )}
      </div>

      {circles.length === 0 ? (
        <NoCircles
          onCreateClick={() => setShowCreate(true)}
          onJoinClick={() => setShowJoin(true)}
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "24px" }}>
          {circles.map(c => (
            <CircleCard key={c.id} circle={c} onClick={() => setActiveCircle(c.id)} />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateModal
          user={user}
          onClose={() => setShowCreate(false)}
          onCreated={d => {
            setShowCreate(false);
            setShowCreated(d);
            loadCircles();
          }}
        />
      )}

      {showJoin && (
        <JoinModal
          user={user}
          onClose={() => setShowJoin(false)}
          onJoined={d => {
            setShowJoin(false);
            loadCircles();
            if (d.circle_id) setActiveCircle(d.circle_id);
          }}
        />
      )}

      {showCreated && (
        <CreatedModal
          circle={showCreated}
          onClose={() => {
            setActiveCircle(showCreated.circle_id);
            setShowCreated(null);
          }}
        />
      )}
    </div>
  );
}
